import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import toast from "react-hot-toast";
import { PiMagnifyingGlass, PiWarningCircle, PiX } from "react-icons/pi";
import { useFloating, autoUpdate, offset, flip, shift, size, useDismiss, useRole, useInteractions, FloatingPortal } from "@floating-ui/react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import API from "../api";
import { getSetting } from "../queries/setting";
import { EXAM_TYPE_COLOR_BG, EXAM_TYPE_COLOR_TEXT, EXAM_TYPE_COLOR_BORDER } from "../utils";
import { capture } from "../instrument";
import { usePrescriptionHover } from "./PrescriptionHoverContext";
import RenewalPopover from "./admission/RenewalPopover";

const MIN_QUERY_LENGTH = 1;
const DEBOUNCE_MS = 200;
const STALE_TIME_MS = 60_000;
const POPOVER_MIN_WIDTH = 320;
const POPOVER_MAX_WIDTH = 520;
const POPOVER_Z_INDEX = 60;

const CodeButton = ({
  code,
  prescriptionSelectedCodes,
  codeButtonRefs,
  onContextMenu,
  onCodeHover,
  onCodeUnhover,
  registerCodeElement,
  onClick,
  icon,
  ghost = false,
  inCapsule = false,
}) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    const element = buttonRef.current;
    if (!element) return;

    const cleanup = registerCodeElement(code.value, element);
    return cleanup;
  }, [code.value, registerCodeElement]);

  const isSelected = prescriptionSelectedCodes.includes(code.value);

  return (
    <div ref={codeButtonRefs.current[code.value]} className={`inline-block ${inCapsule ? "" : "my-[3px]"}`}>
      <button
        ref={buttonRef}
        type="button"
        tabIndex={code.isRenewalCode ? -1 : undefined}
        className={`flex items-center gap-1 rounded-md border transition-colors duration-100 focus:outline-none querco_${code.value} ${ghost ? "px-1.5 py-0.5 text-xs" : "px-1"} ${
          code.isRenewalCode
            ? "cursor-default border-gray-200 bg-white text-gray-600 shadow-[0_1px_1px_rgba(16,24,40,0.04)]"
            : `focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-0 ${
                isSelected
                  ? "border-primary-200 bg-primary-100 text-primary-700"
                  : code.warning
                    ? "border-orange-100 bg-orange-50 text-orange-700 hover:bg-orange-100"
                    : code.ALD
                      ? "border-purple-100 bg-purple-50 text-purple-700 hover:bg-purple-100"
                      : "border-gray-100 bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`
        }`}
        onContextMenu={onContextMenu}
        onMouseOver={() => onCodeHover(code.value)}
        onMouseOut={() => onCodeUnhover(code.value)}
        onClick={onClick}
      >
        {code.value}
        {icon}
      </button>
    </div>
  );
};

const CodeSuggestionRow = ({ suggestion, selected }) => (
  <div
    className={`flex items-center gap-2 px-3 py-1.5 text-[13px] leading-snug ${selected ? "text-primary-700" : "text-gray-700"}`}
    title={suggestion.name ? `${suggestion.value} — ${suggestion.name}` : suggestion.value}
  >
    <span className={`shrink-0 font-semibold ${selected ? "text-primary-700" : "text-gray-900"}`}>{suggestion.value}</span>
    {suggestion.name && <span className="truncate">{suggestion.name}</span>}
    {suggestion.type && (
      <span
        className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${EXAM_TYPE_COLOR_BG[suggestion.type] || ""} ${
          EXAM_TYPE_COLOR_TEXT[suggestion.type] || ""
        } ${EXAM_TYPE_COLOR_BORDER[suggestion.type] || ""}`}
      >
        {suggestion.type}
      </span>
    )}
    {suggestion.codeType && (
      <span
        className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${
          suggestion.codeType === "groupe" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
        }`}
      >
        {suggestion.codeType === "groupe" ? "G" : "A"}
      </span>
    )}
    {suggestion.examValue && <span className="truncate text-[11px] text-gray-400">{suggestion.examValue}</span>}
  </div>
);

const PrescriptionCodeLookup = ({
  prescription,
  onAdmissionChange,
  selectedCodes,
  setSelectedCodes,
  setContextMenu,
  renewalPopoverCode,
  onRenewalPopoverChange,
  locked = false,
  icon = () => null,
  actionsApplied = false,
  incompatibleCodesRefused = [],
  ghost = false,
}) => {
  const { onCodeHover, onCodeUnhover, registerCodeElement } = usePrescriptionHover();
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectionRect, setSelectionRect] = useState(null);
  const [isConfirmingRenewalRemoval, setIsConfirmingRenewalRemoval] = useState(false);
  const [refusedCode, setRefusedCode] = useState(null);

  const { data: setting } = useQuery({
    queryKey: ["setting"],
    queryFn: getSetting,
    staleTime: STALE_TIME_MS,
  });
  const forcedCodesEnabled = setting?.allowForcedCodes !== false;

  const inputRef = useRef(null);
  const codesContainerRef = useRef(null);
  const codeButtonRefs = useRef({});
  const startPointRef = useRef({ x: 0, y: 0 });
  const endPointRef = useRef({ x: 0, y: 0 });
  const componentRef = useRef(null);
  const selectedItemRef = useRef(null);
  const shiftAnchorRef = useRef(null);

  const codes = useMemo(() => {
    const map = new Map();
    codeButtonRefs.current = {};
    for (const act of prescription.acts || []) {
      for (const codeValue of act.codes || []) {
        const prev = map.get(codeValue) || { value: codeValue, warning: false, ALD: false, renewal: { enabled: false }, actId: null, isRenewalCode: false };
        map.set(codeValue, {
          value: codeValue,
          warning: prev.warning || !!act.warning,
          ALD: prev.ALD || !!act.ALD,
          renewal: prev.renewal?.enabled ? prev.renewal : act.renewal,
          actId: prev.actId || act._id,
          isRenewalCode: prev.isRenewalCode || !!act.renewalId,
        });
      }
    }
    return Array.from(map.values());
  }, [prescription.acts]);

  const codeGroups = useMemo(() => {
    const groups = [];
    const renewalGroups = new Map();
    for (const code of codes) {
      if (code.isRenewalCode) continue;
      if (!code.renewal?.enabled) {
        groups.push({ key: code.value, codes: [code] });
        continue;
      }
      const key = `${code.renewal.count}@${code.renewal.deadline}`;
      if (!renewalGroups.has(key)) {
        renewalGroups.set(key, { key, renewal: code.renewal, codes: [] });
        groups.push(renewalGroups.get(key));
      }
      renewalGroups.get(key).codes.push(code);
    }
    return groups;
  }, [codes]);

  const renewalCodes = codes.filter((code) => code.isRenewalCode);

  const prescriptionSelectedCodes = useMemo(() => {
    return selectedCodes.filter((codeId) => codeId.startsWith(`${prescription._id}:`)).map((codeId) => codeId.split(":")[1]);
  }, [selectedCodes, prescription._id]);

  useEffect(() => {
    codes.forEach((code) => {
      if (!codeButtonRefs.current[code.value]) {
        codeButtonRefs.current[code.value] = React.createRef();
      }
    });
  }, [codes]);

  const codeFilterKey = useMemo(
    () =>
      codes
        .map((c) => c.value)
        .sort()
        .join(","),
    [codes],
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInputValue(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const normalizedQuery = debouncedInputValue.trim();
  const queryEnabled = normalizedQuery.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, error } = useQuery({
    queryKey: ["prescription-code", codeFilterKey, normalizedQuery],
    queryFn: async ({ signal }) => {
      const encodedQuery = encodeURIComponent(normalizedQuery);
      const response = await API.get(`/code?q=${encodedQuery}&filterOutCodes=${codeFilterKey}&enabledAtFrontDesk=true`, { signal });
      if (!response.ok) {
        if (response.message) toast.error(response.message);
        return [];
      }
      return response.data || [];
    },
    enabled: queryEnabled,
    retry: false,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  const suggestions = queryEnabled ? data || [] : [];
  const isLoading = queryEnabled && isFetching;

  useEffect(() => {
    if (!error) return;
    if (error?.name === "AbortError") return;
    capture(error);
    toast.error("Une erreur est survenue");
  }, [error]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [normalizedQuery]);

  useEffect(() => {
    if (selectedItemRef.current) selectedItemRef.current.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const notices = [
    refusedCode && `Code « ${refusedCode} » refusé : absent du référentiel`,
    ...incompatibleCodesRefused.map((refusal) => `Code « ${refusal.codes.join(", ")} » refusé : incompatible avec ${refusal.incompatibleWith.join(", ")}`),
    actionsApplied && "Une action a modifié les codes de cette prescription",
  ].filter(Boolean);

  const hasQuery = inputValue.trim().length >= MIN_QUERY_LENGTH;
  const showPopover = !locked && isFocused && hasQuery && !userDismissed;

  const addCode = (code) => {
    if (!code?.value) return;
    if (codes.find((c) => c.value === code.value)) {
      toast.error("Le code est déjà présent");
      return;
    }
    onAdmissionChange([
      {
        field: `prescriptions.${prescription._id}.acts`,
        value: [...(prescription.acts || []), { codes: [code.value], isAdded: true, sampleId: code.sampleId }],
      },
    ]);
    setRefusedCode(null);
    setInputValue("");
    inputRef.current?.focus();
  };

  const validateAndAddManualCode = async (rawInput) => {
    const trimmed = rawInput.trim();
    if (!trimmed) return;
    try {
      const encodedQuery = encodeURIComponent(trimmed);
      const response = await API.get(`/code?q=${encodedQuery}&enabledAtFrontDesk=true`);
      const exactMatch = response.ok ? (response.data || []).find((c) => c.value.toUpperCase() === trimmed.toUpperCase()) : null;
      if (exactMatch) return addCode(exactMatch);
      if (forcedCodesEnabled) return addCode({ value: trimmed });
      setRefusedCode(trimmed.toUpperCase());
    } catch (fetchError) {
      capture(fetchError);
      if (forcedCodesEnabled) return addCode({ value: trimmed });
      setRefusedCode(trimmed.toUpperCase());
    }
  };

  const { refs, floatingStyles, context } = useFloating({
    open: showPopover,
    onOpenChange: (next) => {
      if (next === false) setUserDismissed(true);
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          const width = Math.max(POPOVER_MIN_WIDTH, Math.min(POPOVER_MAX_WIDTH, rects.reference.width));
          Object.assign(elements.floating.style, {
            width: `${width}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  const normalized = inputValue.trim().toLowerCase();
  const hasExactMatch = suggestions.some((s) => (s.value || "").toLowerCase() === normalized);
  const footerVisible = normalized.length >= MIN_QUERY_LENGTH && hasExactMatch === false;
  const totalItems = suggestions.length + (footerVisible ? 1 : 0);
  const footerSelected = footerVisible && selectedIndex === suggestions.length;

  const navOpts = { enabled: showPopover && totalItems > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys(
    "down",
    () => {
      setIsKeyboardNavigating(true);
      setSelectedIndex((i) => Math.min(totalItems - 1, i + 1));
    },
    navOpts,
    [showPopover, totalItems],
  );
  useHotkeys(
    "up",
    () => {
      setIsKeyboardNavigating(true);
      setSelectedIndex((i) => Math.max(0, i - 1));
    },
    navOpts,
    [showPopover, totalItems],
  );
  useHotkeys(
    "enter",
    () => {
      if (footerSelected === true) validateAndAddManualCode(inputValue);
      else if (suggestions[selectedIndex]) addCode(suggestions[selectedIndex]);
    },
    navOpts,
    [showPopover, totalItems, footerSelected, suggestions, selectedIndex, inputValue],
  );

  const onPanelMouseMove = () => {
    if (isKeyboardNavigating === true) setIsKeyboardNavigating(false);
  };

  const getSelectionRectangle = (start, end) => {
    if (!codesContainerRef.current) return null;
    const containerRect = codesContainerRef.current.getBoundingClientRect();

    const x1 = Math.min(start.x, end.x) - containerRect.left;
    const y1 = Math.min(start.y, end.y) - containerRect.top;
    const x2 = Math.max(start.x, end.x) - containerRect.left;
    const y2 = Math.max(start.y, end.y) - containerRect.top;

    return { left: x1, top: y1, width: x2 - x1, height: y2 - y1 };
  };

  const handleMouseDown = (e) => {
    if (e.button === 2) return;
    const isFromPortal = !e.currentTarget.contains(e.target);
    if (isFromPortal) return;

    e.preventDefault();

    const point = { x: e.clientX, y: e.clientY };
    startPointRef.current = point;
    endPointRef.current = point;

    setIsDragging(true);
    setSelectionRect(null);

    const button = e.target.closest("button");
    if (button?.dataset?.value) {
      const codeValue = button.dataset.value;
      const codeId = `${prescription._id}:${codeValue}`;
      setSelectedCodes((prev) => (prev.includes(codeId) ? prev.filter((c) => c !== codeId) : [...prev, codeId]));
    }
  };

  const onDragMouseMove = useCallback((e) => {
    e.preventDefault();

    const currentEndPoint = { x: e.clientX, y: e.clientY };
    endPointRef.current = currentEndPoint;
    setSelectionRect(getSelectionRectangle(startPointRef.current, currentEndPoint));
  }, []);

  const onDragMouseUp = useCallback(
    (e) => {
      e.preventDefault();

      setIsDragging(false);
      setSelectionRect(null);

      const isClick = Math.abs(startPointRef.current.x - endPointRef.current.x) < 5 && Math.abs(startPointRef.current.y - endPointRef.current.y) < 5;

      if (isClick) {
        const clickedOnButton = e.target.closest("button");
        if (!clickedOnButton) setSelectedCodes((prev) => prev.filter((codeId) => !codeId.startsWith(`${prescription._id}:`)));
        return;
      }

      const rect = getSelectionRectangle(startPointRef.current, endPointRef.current);
      const newlySelectedCodes = [];
      const containerRect = codesContainerRef.current.getBoundingClientRect();

      Object.entries(codeButtonRefs.current).forEach(([codeValue, ref]) => {
        if (codes.find((code) => code.value === codeValue)?.isRenewalCode) return;
        if (ref.current) {
          const buttonRect = ref.current.getBoundingClientRect();
          const relativeButtonRect = {
            top: buttonRect.top - containerRect.top,
            left: buttonRect.left - containerRect.left,
            bottom: buttonRect.bottom - containerRect.top,
            right: buttonRect.right - containerRect.left,
            width: buttonRect.width,
            height: buttonRect.height,
          };

          if (
            relativeButtonRect.left < rect.left + rect.width &&
            relativeButtonRect.left + relativeButtonRect.width > rect.left &&
            relativeButtonRect.top < rect.top + rect.height &&
            relativeButtonRect.top + relativeButtonRect.height > rect.top
          ) {
            newlySelectedCodes.push(`${prescription._id}:${codeValue}`);
          }
        }
      });

      setSelectedCodes((prev) => {
        const other = prev.filter((codeId) => !codeId.startsWith(`${prescription._id}:`));
        return [...other, ...newlySelectedCodes];
      });
    },
    [prescription._id, codes],
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onDragMouseMove);
      window.addEventListener("mouseup", onDragMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", onDragMouseMove);
      window.removeEventListener("mouseup", onDragMouseUp);
    };
  }, [isDragging, onDragMouseMove, onDragMouseUp]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (e.target.closest('[data-context-menu="true"]')) return;
      if (e.target.closest('[data-modal="true"]')) return;

      if (componentRef.current && !componentRef.current.contains(e.target)) {
        const hasPrescriptionCodes = selectedCodes.some((codeId) => codeId.startsWith(`${prescription._id}:`));
        if (hasPrescriptionCodes) {
          setSelectedCodes((prev) => prev.filter((codeId) => !codeId.startsWith(`${prescription._id}:`)));
        }
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [selectedCodes, setSelectedCodes, prescription._id]);

  const renderCode = (code, inCapsule = false) => (
    <CodeButton
      key={code.value}
      code={code}
      ghost={ghost}
      inCapsule={inCapsule}
      prescriptionId={prescription._id}
      prescriptionSelectedCodes={prescriptionSelectedCodes}
      codeButtonRefs={codeButtonRefs}
      onCodeHover={onCodeHover}
      onCodeUnhover={onCodeUnhover}
      registerCodeElement={registerCodeElement}
      icon={code.actId ? icon(code.actId) : null}
      onClick={(e) => {
        if (code.isRenewalCode) return;
        const codeId = `${prescription._id}:${code.value}`;
        const isCtrlOrCmdPressed = e.ctrlKey || e.metaKey;

        if (e.shiftKey && shiftAnchorRef.current) {
          const values = codeGroups.flatMap((group) => group.codes.map((c) => c.value));
          const anchorIndex = values.indexOf(shiftAnchorRef.current);
          const clickedIndex = values.indexOf(code.value);
          if (anchorIndex !== -1 && clickedIndex !== -1) {
            const rangeIds = values.slice(Math.min(anchorIndex, clickedIndex), Math.max(anchorIndex, clickedIndex) + 1).map((value) => `${prescription._id}:${value}`);
            return setSelectedCodes((prev) => {
              const base = isCtrlOrCmdPressed ? prev : prev.filter((cId) => !cId.startsWith(`${prescription._id}:`));
              return [...base, ...rangeIds.filter((id) => !base.includes(id))];
            });
          }
        }

        shiftAnchorRef.current = code.value;
        const isAlreadySelected = selectedCodes.includes(codeId);

        if (isCtrlOrCmdPressed && isAlreadySelected) return setSelectedCodes((prev) => prev.filter((c) => c !== codeId));
        if (isCtrlOrCmdPressed && !isAlreadySelected) return setSelectedCodes((prev) => [...prev, codeId]);

        setSelectedCodes(() => [codeId]);
      }}
      onContextMenu={(e) => {
        if (code.isRenewalCode) return;
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) return;

        e.stopPropagation();
        const codeId = `${prescription._id}:${code.value}`;

        const currentSelection = selectedCodes.includes(codeId) ? selectedCodes : [...selectedCodes, codeId];
        const prescriptionSelectedCodeValues = currentSelection.filter((cId) => cId.startsWith(`${prescription._id}:`)).map((cId) => cId.split(":")[1]);

        let allALD = false;
        let allRenewal = false;
        const relevantCodes = codes.filter((c) => prescriptionSelectedCodeValues.includes(c.value));
        if (relevantCodes.length > 0 && relevantCodes.every((c) => c.ALD)) allALD = true;
        if (relevantCodes.length > 0 && relevantCodes.every((c) => c.renewal?.enabled)) allRenewal = true;

        setSelectedCodes(currentSelection);
        setTimeout(() => setContextMenu({ visible: true, x: e.clientX, y: e.clientY, allSelectedAreALD: allALD, allSelectedAreRenewal: allRenewal }), 50);
      }}
    />
  );

  return (
    <div className="flex flex-1 flex-col gap-1" ref={componentRef}>
      {!ghost && <div className="font-bold">Analyse </div>}
      {renewalCodes.length > 0 && (
        <div className="rounded-lg border border-gray-200/80 bg-gray-50/80 px-2 pb-2 pt-1.5" title="Analyses du renouvellement, saisies telles quelles dans le SIL">
          {isConfirmingRenewalRemoval ? (
            <div className="flex items-center justify-between pb-1.5 text-[11px] font-medium text-gray-700">
              Retirer le renouvellement ?
              <div className="-my-0.5 flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => setIsConfirmingRenewalRemoval(false)}
                  className="rounded px-1.5 py-0.5 text-gray-500 transition-colors hover:bg-gray-200/70 hover:text-gray-700"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsConfirmingRenewalRemoval(false);
                    onAdmissionChange([
                      {
                        field: `prescriptions.${prescription._id}.acts`,
                        value: prescription.acts.map((act) => (act.renewalId ? { ...act, codes: [], isDeleted: true } : act)),
                      },
                    ]);
                  }}
                  className="rounded px-1.5 py-0.5 text-red-600 transition-colors hover:bg-red-50"
                >
                  Retirer
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between pb-1.5 text-[11px] font-medium text-gray-500">
              Renouvellement
              {!locked && (
                <button
                  type="button"
                  title="Retirer le renouvellement"
                  onClick={() => setIsConfirmingRenewalRemoval(true)}
                  className="-m-0.5 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-700"
                >
                  <PiX className="size-3" />
                </button>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-1">{renewalCodes.map((code) => renderCode(code, true))}</div>
        </div>
      )}
      <div
        ref={codesContainerRef}
        className={`relative mb-2 flex flex-wrap items-center gap-1 rounded border border-transparent px-2 ${locked ? "pointer-events-none" : ""}`}
        onMouseDown={handleMouseDown}
      >
        {isDragging && selectionRect && (
          <div
            className="pointer-events-none absolute z-10 border border-primary-500 bg-primary-300 bg-opacity-30"
            style={{
              left: `${selectionRect.left}px`,
              top: `${selectionRect.top}px`,
              width: `${selectionRect.width}px`,
              height: `${selectionRect.height}px`,
            }}
          />
        )}

        {renewalCodes.length > 0 && codeGroups.length > 0 && <div className="w-full pt-1 text-[11px] font-medium text-gray-500">Hors renouvellement</div>}
        {codeGroups.map((group) =>
          group.renewal ? (
            <div
              key={group.codes[0].value}
              className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white p-0.5 transition-colors hover:border-gray-300"
            >
              <RenewalPopover
                renewal={group.renewal}
                isOpen={group.codes.some((code) => code.value === renewalPopoverCode)}
                onOpenChange={(isOpen) => onRenewalPopoverChange(isOpen ? group.codes[0].value : null)}
                onRenewalChange={(nextRenewal) =>
                  onAdmissionChange([
                    {
                      field: `prescriptions.${prescription._id}.acts`,
                      value: prescription.acts.map((act) =>
                        act.renewal?.enabled && `${act.renewal.count}@${act.renewal.deadline}` === group.key ? { ...act, renewal: nextRenewal } : act,
                      ),
                    },
                  ])
                }
              />
              <span className="h-3.5 w-px bg-gray-200" />
              {group.codes.map((code) => renderCode(code, true))}
            </div>
          ) : (
            renderCode(group.codes[0])
          ),
        )}
      </div>

      {!locked && (
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          className={`mt-auto flex w-full items-center gap-2 border border-gray-200 bg-white focus-within:outline-none ${
            ghost ? "min-h-[32px] rounded-md px-2 py-1" : "min-h-[42px] rounded-lg px-3 py-2"
          } ${showPopover ? "rounded-b-none" : ""} cursor-text`}
          onClick={() => inputRef.current?.focus()}
        >
          <PiMagnifyingGlass className="size-4 shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              setUserDismissed(false);
              setRefusedCode(null);
            }}
            onFocus={() => {
              setIsFocused(true);
              setUserDismissed(false);
            }}
            onBlur={() => setTimeout(() => setIsFocused(false), 150)}
            placeholder={prescription.type === "RENEWAL" ? "Ajouter hors renouvellement…" : ghost ? "Rechercher un code…" : "Code analyse"}
            aria-label={ghost ? "Rechercher un code analyse" : "Code analyse"}
            className={`min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none ${ghost ? "placeholder:text-gray-300" : "placeholder:text-gray-400"}`}
          />
          {isLoading && <div className="field-spinner" />}
          {isFocused && (
            <div className="ml-1 hidden items-center gap-1 text-[0.6875rem] text-gray-400 sm:flex">
              <kbd className="rounded border border-gray-200 bg-white px-1 py-px font-sans leading-none">↑↓</kbd>
              <kbd className="rounded border border-gray-200 bg-white px-1 py-px font-sans leading-none">↵</kbd>
              <kbd className="rounded border border-gray-200 bg-white px-1 py-px font-sans leading-none">esc</kbd>
            </div>
          )}
        </div>
      )}

      {notices.length > 0 && (
        <div className="mt-1.5 flex flex-col gap-1">
          {notices.map((notice) => (
            <div key={notice} className="flex items-center gap-2 rounded-md border border-red-100 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
              <PiWarningCircle className="size-3.5 shrink-0 text-red-500" />
              <span>{notice}</span>
            </div>
          ))}
        </div>
      )}

      {showPopover && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: POPOVER_Z_INDEX }} className="flex flex-col" {...getFloatingProps()}>
            <div
              className={`flex min-h-0 flex-1 flex-col overflow-hidden border border-t-0 border-gray-200 bg-white shadow-lg ${ghost ? "rounded-b-md" : "rounded-b-lg"}`}
              data-modal="true"
              onMouseMove={onPanelMouseMove}
            >
              {suggestions.length > 0 && (
                <ul role="list" className="min-h-0 flex-1 overflow-auto py-1">
                  {suggestions.map((suggestion, suggestionIndex) => {
                    const isSelected = suggestionIndex === selectedIndex;
                    const key = suggestion._id || suggestion.value;
                    return (
                      <li
                        key={key}
                        ref={isSelected ? selectedItemRef : null}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          addCode(suggestion);
                        }}
                        onMouseEnter={() => {
                          if (isKeyboardNavigating === false) setSelectedIndex(suggestionIndex);
                        }}
                        className={`relative cursor-pointer select-none ${isSelected ? "bg-primary-50/70 text-primary-700" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <CodeSuggestionRow suggestion={suggestion} selected={isSelected} />
                      </li>
                    );
                  })}
                </ul>
              )}

              {hasQuery && isLoading && suggestions.length === 0 && <div className="suggestion-empty">Recherche…</div>}

              {hasQuery && !isLoading && suggestions.length === 0 && footerVisible === false && <div className="suggestion-empty">Aucun code trouvé</div>}

              {footerVisible === true && (
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    validateAndAddManualCode(inputValue);
                  }}
                  onMouseEnter={() => {
                    if (isKeyboardNavigating === false) setSelectedIndex(suggestions.length);
                  }}
                  className={`flex w-full shrink-0 items-center gap-2 border-t border-gray-100 px-3 py-2 text-left text-[0.8125rem] font-medium ${
                    footerSelected ? "bg-primary-50/70 text-primary-700" : "text-primary-600 hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`flex size-4 shrink-0 items-center justify-center rounded-full border border-dashed leading-none ${
                      footerSelected ? "border-primary-500" : "border-current"
                    }`}
                  >
                    +
                  </span>
                  <span className="truncate">Ajouter « {inputValue.trim().toUpperCase()} » manuellement</span>
                </button>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default PrescriptionCodeLookup;
